<template>
  <div class="pub-cite">
    <p v-if="pub.status" class="pub-cite-venue">{{ pub.status }}</p>
    <h4 class="pub-cite-title">
      <a
        v-if="paperUrl"
        :href="paperUrl"
        target="_blank"
        rel="noopener"
        v-html="hlText(pub.title)"
      ></a>
      <span v-else v-html="hlText(pub.title)"></span>
    </h4>
    <p v-if="pub.authors" class="pub-cite-authors" v-html="hlHtml(authorsHtml)"></p>
    <p v-if="extraLinks.length" class="pub-cite-links">
      <a
        v-for="link in extraLinks"
        :key="link.label"
        class="pub-cite-link"
        :href="link.url"
        target="_blank"
        rel="noopener"
      >
        {{ link.label }}
        <span class="pub-cite-arrow" aria-hidden="true">&rarr;</span>
      </a>
    </p>
    <div v-if="resourceLinks.length" class="pub-cite-resources">
      <GitHubRepoLink
        v-for="link in resourceLinks"
        :key="link.url"
        :href="link.url"
        :label="link.label"
        :icon-src="link.iconSrc || ''"
        :icon-class="link.iconClass || 'fab fa-github'"
      />
    </div>
  </div>
</template>

<script>
import {
  highlightText,
  highlightHtml,
  escapeHtml,
  escapeRegExp
} from '../utils/highlight';
import GitHubRepoLink from './ui/GitHubRepoLink.vue';

export default {
  name: 'PublicationMeta',
  components: { GitHubRepoLink },
  props: {
    pub: {
      type: Object,
      required: true
    },
    searchTerms: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    paperUrl() {
      const paper = (this.pub.links || []).find((link) => link.label === 'Paper');
      return paper ? paper.url : '';
    },
    extraLinks() {
      return (this.pub.links || []).filter((link) => link.label !== 'Paper' && link.presentation !== 'resource');
    },
    resourceLinks() {
      return (this.pub.links || []).filter((link) => link.presentation === 'resource');
    },
    authorsHtml() {
      const text = this.pub.authors || '';
      const escaped = escapeHtml(text);
      if (!this.pub.self) return escaped;
      const re = new RegExp(`(${escapeRegExp(this.pub.self)})`, 'g');
      return escaped.replace(re, '<b>$1</b>');
    }
  },
  methods: {
    hlText(text) {
      return highlightText(text, this.searchTerms);
    },
    hlHtml(html) {
      return highlightHtml(html, this.searchTerms);
    }
  }
};
</script>

<style scoped>
/* The publication citation module: the small block under each research entry
   carrying venue, linked paper title, authors, and extra links. It stays
   inside the page's editorial voice with spacing alone, since the entry rows
   already carry their own hairline separators. */
.pub-cite {
  margin-top: 1.15em;
}
.pub-cite-venue {
  margin: 0 0 0.3em;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.68rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #8a8a8a;
}
.pub-cite-title {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 1.02em;
  line-height: 1.35;
  margin: 0 0 0.3em;
  color: #333;
}
.pub-cite-title a {
  color: #333;
  text-decoration: underline;
  text-decoration-color: rgba(0, 0, 0, 0.25);
  text-underline-offset: 3px;
}
.pub-cite-title a:hover {
  color: #000;
  text-decoration-color: #000;
}
:global(html.theme-transitioning .pub-cite-title a),
:global(body.theme-transitioning .pub-cite-title a) {
  transition:
    color var(--theme-duration, 220ms) ease,
    text-decoration-color var(--theme-duration, 220ms) ease;
}
.pub-cite-authors {
  margin: 0 0 0.45em;
  font-size: 0.88rem;
  color: #777;
}
.pub-cite-links {
  margin: 0;
}
.pub-cite-resources {
  margin-top: 0.55rem;
}
.pub-cite-link {
  margin-right: 1.2em;
  font-size: 0.88rem;
}
.pub-cite-arrow {
  display: inline-block;
  margin-left: 0.15em;
  transition: transform 0.22s var(--ease-out);
}
.pub-cite-link:hover .pub-cite-arrow {
  transform: translateX(3px);
}

body.dark-mode .pub-cite-venue {
  color: var(--site-text-tertiary);
}
body.dark-mode .pub-cite-title,
body.dark-mode .pub-cite-title a {
  color: #cccccc;
}
body.dark-mode .pub-cite-title a {
  text-decoration-color: rgba(255, 255, 255, 0.3);
}
body.dark-mode .pub-cite-title a:hover {
  color: #ffffff;
}
body.dark-mode .pub-cite-authors {
  color: var(--site-text-secondary);
}
</style>
